import { useLocale, useTranslation } from "@ncp/i18n";
import { Field, Input, NativeSelect, Switch, Textarea } from "@ncp/ui";
import type { RecordField, RecordIssue } from "./api";

/** The field a validation issue belongs to ("/cost", "cost" or "$.cost" → "cost"). */
export function issueField(path: string): string {
  return path.replace(/^\$\.?/, "").replace(/^\//, "").split(/[/.[]/)[0] ?? "";
}

export function fieldLabel(f: Pick<RecordField, "label" | "labelAr" | "key">, locale: string): string {
  return (locale === "ar" && f.labelAr ? f.labelAr : f.label) || f.key;
}

/** A record's fields as inputs, driven by the record type; issues from the server show under their field. */
export function RecordForm({ fields, data, onChange, issues = [], readOnly }: { fields: RecordField[]; data: Record<string, unknown>; onChange: (next: Record<string, unknown>) => void; issues?: RecordIssue[]; readOnly?: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const set = (k: string, v: unknown) => onChange({ ...data, [k]: v });
  const msg = (i: RecordIssue) => (locale === "ar" && i.messageAr ? i.messageAr : i.message);
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {fields.map((f) => {
        const err = issues.filter((i) => issueField(i.path) === f.key);
        const value = data[f.key];
        const help = locale === "ar" && f.helpAr ? f.helpAr : f.help ?? undefined;
        const wide = f.type === "longText";
        const input = (() => {
          switch (f.type) {
            case "longText": return <Textarea data-testid={`field-${f.key}`} value={String(value ?? "")} disabled={readOnly} onChange={(e) => set(f.key, e.target.value)} className="min-h-[88px]" />;
            case "number": case "money": return <Input data-testid={`field-${f.key}`} value={value == null ? "" : String(value)} disabled={readOnly} inputMode="decimal" dir="ltr" onChange={(e) => set(f.key, e.target.value)} />;
            case "date": return <Input data-testid={`field-${f.key}`} type="date" value={String(value ?? "")} disabled={readOnly} onChange={(e) => set(f.key, e.target.value)} />;
            case "boolean": return <div className="flex h-9 items-center"><Switch checked={value === true || value === "true"} disabled={readOnly} onCheckedChange={(c) => set(f.key, c)} aria-label={fieldLabel(f, locale)} /></div>;
            case "choice": return (
              <NativeSelect data-testid={`field-${f.key}`} value={String(value ?? "")} disabled={readOnly} onChange={(e) => set(f.key, e.target.value)}>
                <option value="">—</option>
                {(f.options ?? []).map((o) => <option key={o.value} value={o.value}>{locale === "ar" && o.labelAr ? o.labelAr : o.label}</option>)}
              </NativeSelect>
            );
            case "email": return <Input data-testid={`field-${f.key}`} type="email" dir="ltr" value={String(value ?? "")} disabled={readOnly} onChange={(e) => set(f.key, e.target.value)} />;
            case "reference": return <Input data-testid={`field-${f.key}`} dir="ltr" className="font-mono" placeholder={t("admin:records.referenceHint", { type: f.target ?? "" })} value={String(value ?? "")} disabled={readOnly} onChange={(e) => set(f.key, e.target.value)} />;
            default: return <Input data-testid={`field-${f.key}`} value={String(value ?? "")} disabled={readOnly} onChange={(e) => set(f.key, e.target.value)} />;
          }
        })();
        return (
          <div key={f.key} className={wide ? "sm:col-span-2" : undefined}>
            <Field label={fieldLabel(f, locale)} required={f.required} hint={help} error={err.length ? err.map(msg).join(" ") : undefined}>{input}</Field>
          </div>
        );
      })}
    </div>
  );
}
